<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import { debounce } from 'lodash'
import axios from 'axios'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import MyTutors from '@/Components/Student/Dashboard/MyTutors.vue'
import ContentCard, { type ContentItem } from '@/Components/Student/ContentCard.vue'
import ContentListItem from '@/Components/Student/ContentListItem.vue'
import {
  RotateCw,
  Grid3X3,
  List,
  Search,
  Loader2,
  X,
  Video,
  ChevronLeft,
  ChevronRight,
} from 'lucide-vue-next'

interface Tutor {
  id: number
  name: string
  slug: string
  avatar_url: string | null
  subject: string
}

interface Subject {
  id: number
  name: string
  slug: string
  color: string
}

interface Level {
  id: number
  name: string
  slug: string
}

interface Props {
  tutors: Tutor[]
  subjects: Subject[]
  levels: Level[]
  subscriptionCount: number
}

const props = defineProps<Props>()

// State
const contents = ref<ContentItem[]>([])
const loading = ref(false)
const initialLoading = ref(true)
const viewMode = ref<'grid' | 'list'>('grid')

// Get initial values from URL query string
function getQueryParam(key: string): string | null {
  const params = new URLSearchParams(window.location.search)
  return params.get(key)
}

function getQueryParamAsNumber(key: string): number | null {
  const value = getQueryParam(key)
  return value ? parseInt(value, 10) : null
}

// Filters - initialize from URL query string
const selectedTutorId = ref<number | null>(getQueryParamAsNumber('tutor_id'))
const selectedSubjectId = ref<number | null>(getQueryParamAsNumber('subject_id'))
const selectedLevelId = ref<number | null>(getQueryParamAsNumber('level_id'))
const selectedType = ref<string | null>(getQueryParam('type'))
const sortBy = ref(getQueryParam('sort') || 'recent')
const searchQuery = ref(getQueryParam('search') || '')

// Pagination
const currentPage = ref(getQueryParamAsNumber('page') || 1)
const lastPage = ref(1)
const total = ref(0)
const perPage = ref(12)

// Active filters for display
const activeFilters = computed(() => {
  const filters: { key: string; label: string; value: string | number }[] = []

  if (selectedSubjectId.value) {
    const subject = props.subjects.find(s => s.id === selectedSubjectId.value)
    if (subject) filters.push({ key: 'subject', label: subject.name, value: selectedSubjectId.value })
  }

  if (selectedTutorId.value) {
    const tutor = props.tutors.find(t => t.id === selectedTutorId.value)
    if (tutor) filters.push({ key: 'tutor', label: tutor.name, value: selectedTutorId.value })
  }

  if (selectedLevelId.value) {
    const level = props.levels.find(l => l.id === selectedLevelId.value)
    if (level) filters.push({ key: 'level', label: level.name, value: selectedLevelId.value })
  }

  if (selectedType.value) {
    filters.push({ key: 'type', label: selectedType.value, value: selectedType.value })
  }

  return filters
})

const hasActiveFilters = computed(() => activeFilters.value.length > 0 || searchQuery.value.trim() !== '')

const isEmpty = computed(() => !loading.value && !initialLoading.value && contents.value.length === 0)

const hasNoSubscriptions = computed(() => props.subscriptionCount === 0)

// Build query params object
function buildQueryParams(): Record<string, any> {
  const params: Record<string, any> = {}

  if (selectedTutorId.value) params.tutor_id = selectedTutorId.value
  if (selectedSubjectId.value) params.subject_id = selectedSubjectId.value
  if (selectedLevelId.value) params.level_id = selectedLevelId.value
  if (selectedType.value) params.type = selectedType.value
  if (sortBy.value && sortBy.value !== 'recent') params.sort = sortBy.value
  if (searchQuery.value.trim()) params.search = searchQuery.value.trim()
  if (currentPage.value > 1) params.page = currentPage.value

  return params
}

// Update URL with current filters (without page reload)
function updateUrl() {
  const params = buildQueryParams()
  const queryString = new URLSearchParams(params as Record<string, string>).toString()
  const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname

  window.history.replaceState({}, '', newUrl)
}

// Fetch content
async function fetchContent(page: number = 1) {
  if (page === 1) {
    initialLoading.value = true
  } else {
    loading.value = true
  }

  try {
    const params: Record<string, any> = { page, per_page: perPage.value }

    if (selectedTutorId.value) params.tutor_id = selectedTutorId.value
    if (selectedSubjectId.value) params.subject_id = selectedSubjectId.value
    if (selectedLevelId.value) params.level_id = selectedLevelId.value
    if (selectedType.value) params.type = selectedType.value
    if (sortBy.value) params.sort = sortBy.value
    if (searchQuery.value.trim()) params.search = searchQuery.value.trim()

    const response = await axios.get('/student/resources/content', { params })

    contents.value = response.data.data
    currentPage.value = response.data.meta.current_page
    lastPage.value = response.data.meta.last_page
    total.value = response.data.meta.total

    // Update URL after successful fetch
    updateUrl()
  } catch (error) {
    console.error('Failed to fetch content:', error)
  } finally {
    loading.value = false
    initialLoading.value = false
  }
}

// Apply filters
function applyFilters() {
  currentPage.value = 1
  fetchContent(1)
}

// Reset filters
function resetFilters() {
  selectedTutorId.value = null
  selectedSubjectId.value = null
  selectedLevelId.value = null
  selectedType.value = null
  sortBy.value = 'recent'
  searchQuery.value = ''
  currentPage.value = 1
  fetchContent(1)
}

// Remove specific filter
function removeFilter(key: string) {
  if (key === 'subject') selectedSubjectId.value = null
  if (key === 'tutor') selectedTutorId.value = null
  if (key === 'level') selectedLevelId.value = null
  if (key === 'type') selectedType.value = null
  applyFilters()
}

// Pagination
function goToPage(page: number) {
  if (page >= 1 && page <= lastPage.value) {
    currentPage.value = page
    fetchContent(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Debounced search
const debouncedSearch = debounce(() => {
  currentPage.value = 1
  fetchContent(1)
}, 500)

watch(searchQuery, () => {
  debouncedSearch()
})

watch(sortBy, () => {
  applyFilters()
})

// Watch filter changes to auto-apply
watch([selectedTutorId, selectedSubjectId, selectedLevelId, selectedType], () => {
  applyFilters()
})

onMounted(() => {
  if (!hasNoSubscriptions.value) {
    fetchContent(currentPage.value)
  } else {
    initialLoading.value = false
  }
})

// Generate page numbers for pagination
const pageNumbers = computed(() => {
  const pages: (number | string)[] = []
  const maxVisible = 5

  if (lastPage.value <= maxVisible) {
    for (let i = 1; i <= lastPage.value; i++) pages.push(i)
  } else {
    if (currentPage.value <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i)
      pages.push('...')
      pages.push(lastPage.value)
    } else if (currentPage.value >= lastPage.value - 2) {
      pages.push(1)
      pages.push('...')
      for (let i = lastPage.value - 3; i <= lastPage.value; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('...')
      for (let i = currentPage.value - 1; i <= currentPage.value + 1; i++) pages.push(i)
      pages.push('...')
      pages.push(lastPage.value)
    }
  }

  return pages
})
</script>

<template>
  <Head title="My Resources" />

  <DashboardLayout>
    <div class="space-y-6">
      <!-- My Tutors - Horizontal Scroll -->
      <MyTutors :tutors="tutors" />

      <!-- No Subscriptions State -->
      <div v-if="hasNoSubscriptions" class="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Video class="w-8 h-8 text-gray-400" />
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">No Active Subscriptions</h3>
        <p class="text-sm text-gray-500 mb-4 max-w-md mx-auto">
          Subscribe to tutors to access their exclusive content library including videos, study materials, and more.
        </p>
        <Link
          href="/student/explore"
          class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          Explore Tutors
        </Link>
      </div>

      <template v-else>
        <!-- Filter Section -->
        <section class="bg-white rounded-xl border border-gray-200 p-6">
          <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
            <!-- Subject Filter -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-2">Subject</label>
              <select
                v-model="selectedSubjectId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option :value="null">All Subjects</option>
                <option v-for="subject in subjects" :key="subject.id" :value="subject.id">
                  {{ subject.name }}
                </option>
              </select>
            </div>

            <!-- Tutor Filter -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-2">Tutor</label>
              <select
                v-model="selectedTutorId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option :value="null">All Tutors</option>
                <option v-for="tutor in tutors" :key="tutor.id" :value="tutor.id">
                  {{ tutor.name }}
                </option>
              </select>
            </div>

            <!-- Content Type Filter -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-2">Content Type</label>
              <select
                v-model="selectedType"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option :value="null">All Types</option>
                <option value="video">Video Lessons</option>
                <option value="document">Study Materials</option>
              </select>
            </div>

            <!-- Level Filter -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-2">Level</label>
              <select
                v-model="selectedLevelId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option :value="null">All Levels</option>
                <option v-for="level in levels" :key="level.id" :value="level.id">
                  {{ level.name }}
                </option>
              </select>
            </div>

            <!-- Sort By -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-2">Sort By</label>
              <select
                v-model="sortBy"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option value="recent">Recently Added</option>
                <option value="title_asc">Title A-Z</option>
                <option value="title_desc">Title Z-A</option>
                <option value="popular">Most Popular</option>
                <option value="duration">Duration</option>
              </select>
            </div>
          </div>

          <!-- Filter Actions -->
          <div class="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <div class="flex items-center gap-2">
              <button
                v-if="hasActiveFilters"
                @click="resetFilters"
                class="px-3 py-1.5 text-gray-600 text-xs font-semibold hover:text-gray-800 transition-colors flex items-center gap-1"
              >
                <RotateCw class="w-3 h-3" />
                Reset Filters
              </button>
            </div>

            <div class="flex items-center gap-3">
              <span class="text-sm text-gray-600">{{ total }} items found</span>
              <div class="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                <button
                  @click="viewMode = 'grid'"
                  :class="[
                    'p-1.5 rounded transition-colors',
                    viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  ]"
                >
                  <Grid3X3 class="w-4 h-4" />
                </button>
                <button
                  @click="viewMode = 'list'"
                  :class="[
                    'p-1.5 rounded transition-colors',
                    viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  ]"
                >
                  <List class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Active Filters -->
        <section v-if="activeFilters.length > 0" class="flex items-center gap-2">
          <span class="text-sm font-semibold text-gray-700">Active Filters:</span>
          <div class="flex items-center gap-2 flex-wrap">
            <span
              v-for="filter in activeFilters"
              :key="filter.key"
              class="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full"
            >
              {{ filter.label }}
              <button @click="removeFilter(filter.key)" class="ml-1.5 hover:text-blue-900">
                <X class="w-3 h-3" />
              </button>
            </span>
            <button
              @click="resetFilters"
              class="text-xs text-gray-500 hover:text-gray-700 font-medium"
            >
              Clear all
            </button>
          </div>
        </section>

        <!-- Loading Skeleton -->
        <section v-if="initialLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="i in 6"
            :key="i"
            class="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse"
          >
            <div class="h-48 bg-gray-200" />
            <div class="p-5 space-y-3">
              <div class="flex gap-2">
                <div class="h-5 w-16 bg-gray-200 rounded" />
                <div class="h-5 w-12 bg-gray-200 rounded" />
              </div>
              <div class="h-5 bg-gray-200 rounded w-3/4" />
              <div class="h-4 bg-gray-200 rounded w-1/2" />
              <div class="h-10 bg-gray-200 rounded" />
            </div>
          </div>
        </section>

        <!-- Empty State -->
        <section
          v-else-if="isEmpty"
          class="bg-white rounded-xl border border-gray-200 p-12 text-center"
        >
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search class="w-8 h-8 text-gray-400" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">No Content Found</h3>
          <p class="text-sm text-gray-500 max-w-md mx-auto">
            <template v-if="hasActiveFilters">
              No content matches your search criteria. Try adjusting your filters.
            </template>
            <template v-else>
              Your subscribed tutors haven't published any content yet. Check back soon!
            </template>
          </p>
          <button
            v-if="hasActiveFilters"
            @click="resetFilters"
            class="mt-4 px-4 py-2 text-blue-600 text-sm font-semibold hover:underline"
          >
            Clear Filters
          </button>
        </section>

        <!-- Content Grid -->
        <section
          v-else-if="viewMode === 'grid'"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <ContentCard
            v-for="(content, index) in contents"
            :key="content.id"
            :content="content"
            :index="index"
          />
        </section>

        <!-- Content List -->
        <section v-else class="space-y-3">
          <ContentListItem
            v-for="(content, index) in contents"
            :key="content.id"
            :content="content"
            :index="index"
          />
        </section>

        <!-- Loading Overlay -->
        <div
          v-if="loading && !initialLoading"
          class="fixed inset-0 bg-white/50 flex items-center justify-center z-50"
        >
          <Loader2 class="w-8 h-8 animate-spin text-blue-600" />
        </div>

        <!-- Pagination -->
        <section v-if="lastPage > 1 && !initialLoading" class="flex items-center justify-center gap-2">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft class="w-4 h-4" />
          </button>

          <template v-for="page in pageNumbers" :key="page">
            <span v-if="page === '...'" class="px-2 text-gray-500">...</span>
            <button
              v-else
              @click="goToPage(page as number)"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                currentPage === page
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
          </template>

          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === lastPage"
            class="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight class="w-4 h-4" />
          </button>
        </section>
      </template>
    </div>
  </DashboardLayout>
</template>
